import {
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  Checkbox,
  Badge,
  cn,
} from '@design-system';
import { Settings, Trash2 } from 'lucide-react';
import {
  QuestionSettingsDialog,
  useQuestionField,
  useFormQuestions,
  getFormBuilderActions,
} from '@/features/quote-and-apply-builder';

interface BuilderQuestionFieldProps {
  section: PolicyFormSection;
  subsection: PolicyFormSubSection;
  group: PolicyFormQuestionGroup;
  question: PolicyFormQuestion;
}

function FieldPreview({ question }: { question: PolicyFormQuestion }) {
  switch (question.type) {
    case 'text':
    case 'number':
      return (
        <Input
          disabled
          placeholder={question.placeholder || 'Enter value...'}
        />
      );
    case 'textarea':
    case 'textarea-text':
    case 'textarea-append':
      return (
        <div className="h-16 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-400">
          {question.placeholder || 'Enter text...'}
        </div>
      );
    case 'select':
    case 'textselect':
      return (
        <Select disabled>
          <SelectTrigger>
            <SelectValue placeholder={question.placeholder || 'Select an option...'} />
          </SelectTrigger>
        </Select>
      );
    case 'radio':
      return (
        <div className="flex flex-wrap gap-4">
          {(question.options || []).slice(0, 3).map(opt => (
            <div
              key={opt.id}
              className="flex items-center gap-2"
            >
              <div className="h-4 w-4 rounded-full border border-gray-300" />
              <span className="text-sm text-gray-600">{opt.value}</span>
            </div>
          ))}
          {(question.options?.length || 0) > 3 && (
            <span className="text-sm text-gray-400">+{question.options!.length - 3} more</span>
          )}
        </div>
      );
    case 'multiselect':
      return (
        <div className="flex flex-wrap gap-2">
          {(question.options || []).slice(0, 3).map(opt => (
            <div
              key={opt.id}
              className="flex items-center gap-1.5 rounded border border-gray-200 bg-white px-2 py-1"
            >
              <Checkbox disabled />
              <span className="text-xs text-gray-600">{opt.value}</span>
            </div>
          ))}
          {(question.options?.length || 0) > 3 && (
            <span className="text-xs text-gray-400">+{question.options!.length - 3} more</span>
          )}
        </div>
      );
    case 'date':
      return (
        <Input
          disabled
          placeholder="MM/DD/YYYY"
        />
      );
    case 'table':
      return (
        <div className="rounded border border-gray-200 bg-white p-2 text-center text-sm text-gray-400">
          Table with {question.columns?.length || 0} columns
        </div>
      );
    default:
      return (
        <Input
          disabled
          placeholder="Field preview..."
        />
      );
  }
}

export function BuilderQuestionField({
  section,
  subsection,
  group,
  question,
}: BuilderQuestionFieldProps) {
  const allQuestions = useFormQuestions();
  const { removeQuestion } = getFormBuilderActions();
  const {
    showSettings,
    isSelected,
    typeLabel,
    validationLabel,
    hasConditions,
    isGridFormat,
    handleSelect,
    handleSaveSettings,
    handleOpenSettings,
    handleCloseSettings,
  } = useQuestionField({ section, subsection, group, question });

  return (
    <div
      className={cn(
        'rounded-md border bg-white p-3 transition-all',
        isSelected
          ? 'border-primary ring-primary/20 ring-2'
          : 'border-gray-200 hover:border-gray-300',
        isGridFormat && 'flex-1',
      )}
      onClick={event => {
        event.stopPropagation();
        if (!isSelected) handleSelect();
      }}
    >
      <div className="mb-2 flex items-start gap-2">
        <div className="flex-1">
          <span className="text-sm font-medium text-gray-950">
            {question.title || 'Untitled Question'}
          </span>

          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
            <Badge variant="secondary">{typeLabel}</Badge>
            {question.required && <Badge variant="destructive">Required</Badge>}
            {validationLabel && <Badge variant="default">{validationLabel}</Badge>}
            {question.hidden && <Badge variant="warning">Hidden</Badge>}
            {question.disabled && <Badge variant="secondary">Disabled</Badge>}
            {hasConditions && <Badge variant="success">Conditions</Badge>}
            {question.composite && <Badge variant="default">Combined</Badge>}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={handleOpenSettings}
            title="Question Settings"
          >
            <Settings className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={event => {
              event.stopPropagation();
              removeQuestion(section.id, subsection.id, group.id, question.questionRef);
            }}
            title="Delete Question"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="ml-0">
        <FieldPreview question={question} />
      </div>

      <QuestionSettingsDialog
        open={showSettings}
        question={question}
        allQuestions={allQuestions}
        onSave={handleSaveSettings}
        onClose={handleCloseSettings}
      />
    </div>
  );
}
